"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkLine = checkLine;
exports.checkBalanced = checkBalanced;
exports.checkPeriod = checkPeriod;
exports.checkCashboxes = checkCashboxes;
const money_1 = require("../money");
/** حسابات تفرض وجود طرف مقابل عند الترحيل عليها. */
const COUNTERPARTY_SUBLEDGERS = new Set(['customer', 'supplier', 'employee']);
/** فحوص السطر الواحد: المبالغ، الحساب، العملة، الطرف المقابل، الضريبة. */
function checkLine(line, lineIndex, ctx, options = {}) {
    const issues = [];
    const account = ctx.accounts.get(line.accountCode);
    if (!account) {
        issues.push({
            code: 'ACCOUNT_NOT_FOUND',
            message: 'account ' + line.accountCode + ' is not in the company chart',
            lineIndex,
            accountCode: line.accountCode,
        });
    }
    else {
        if (!account.isPostable) {
            issues.push({
                code: 'ACCOUNT_NOT_POSTABLE',
                message: 'account ' + account.code + ' is a parent account and cannot be posted to',
                lineIndex,
                accountCode: account.code,
            });
        }
        if (!account.status.startsWith('active')) {
            issues.push({
                code: 'ACCOUNT_INACTIVE',
                message: 'account ' + account.code + ' is not active',
                lineIndex,
                accountCode: account.code,
            });
        }
        if (account.subledger && COUNTERPARTY_SUBLEDGERS.has(account.subledger) && !line.counterparty) {
            issues.push({
                code: 'MISSING_COUNTERPARTY',
                message: 'control account ' + account.code + ' requires a counterparty reference',
                lineIndex,
                accountCode: account.code,
            });
        }
        if (account.subledger === 'cashbox' && !line.cashboxId) {
            issues.push({
                code: 'MISSING_CASHBOX',
                message: 'cashbox account ' + account.code + ' requires cashboxId',
                lineIndex,
                accountCode: account.code,
            });
        }
        if (account.subledger === 'tax' && !line.tax && !options.taxSnapshotOptional) {
            issues.push({
                code: 'MISSING_TAX_SNAPSHOT',
                message: 'tax account ' + account.code + ' requires a versioned tax snapshot',
                lineIndex,
                accountCode: account.code,
            });
        }
    }
    if (line.debit.isNegative() || line.credit.isNegative() || line.amountCurrency.isNegative()) {
        issues.push({
            code: 'NEGATIVE_AMOUNT',
            message: 'negative amounts are not allowed on a journal line',
            lineIndex,
            accountCode: line.accountCode,
        });
    }
    const hasDebit = line.debit.isPositive();
    const hasCredit = line.credit.isPositive();
    if (hasDebit && hasCredit) {
        issues.push({
            code: 'LINE_BOTH_SIDES',
            message: 'a journal line carries either a debit or a credit, never both',
            lineIndex,
            accountCode: line.accountCode,
        });
    }
    if (!hasDebit && !hasCredit) {
        issues.push({
            code: 'LINE_NO_AMOUNT',
            message: 'a journal line must carry a non-zero debit or credit',
            lineIndex,
            accountCode: line.accountCode,
        });
    }
    if (!line.currencyRate.isPositive()) {
        issues.push({
            code: 'INVALID_RATE',
            message: 'currency rate must be greater than zero',
            lineIndex,
            accountCode: line.accountCode,
        });
    }
    else {
        if (line.currency === ctx.baseCurrency && !line.currencyRate.eq('1')) {
            issues.push({
                code: 'CURRENCY_MISMATCH',
                message: 'base currency lines must use a rate of 1',
                lineIndex,
                accountCode: line.accountCode,
            });
        }
        const baseAmount = hasDebit ? line.debit : line.credit;
        const converted = line.amountCurrency.mul(line.currencyRate).round(money_1.MONEY_SCALE, 'half_even');
        if (!converted.eq(baseAmount.normalize())) {
            issues.push({
                code: 'RATE_CONVERSION_MISMATCH',
                message: 'amount_currency * rate does not equal the base amount',
                lineIndex,
                accountCode: line.accountCode,
                details: { expected: converted.toString(), actual: baseAmount.toString() },
            });
        }
    }
    return { issues, account };
}
/** توازن القيد: مجموع المدين = مجموع الدائن بعملة الشركة. */
function checkBalanced(lines) {
    if (lines.length < 2) {
        return [{ code: 'TOO_FEW_LINES', message: 'a posted journal needs at least two lines' }];
    }
    const totalDebit = money_1.Money.sum(lines.map((line) => line.debit)).normalize();
    const totalCredit = money_1.Money.sum(lines.map((line) => line.credit)).normalize();
    if (!totalDebit.eq(totalCredit)) {
        return [
            {
                code: 'UNBALANCED_JOURNAL',
                message: 'journal is not balanced',
                details: { totalDebit: totalDebit.toString(), totalCredit: totalCredit.toString() },
            },
        ];
    }
    if (totalDebit.isZero()) {
        return [{ code: 'LINE_NO_AMOUNT', message: 'journal total cannot be zero' }];
    }
    return [];
}
/** الفترة المالية مفتوحة وتاريخ الترحيل داخلها. */
function checkPeriod(postingDate, ctx) {
    const issues = [];
    const period = ctx.fiscalPeriod;
    if (period.status !== 'OPEN') {
        issues.push({
            code: 'PERIOD_CLOSED',
            message: 'fiscal period ' + period.id + ' is closed',
        });
    }
    if (postingDate < period.startDate || postingDate > period.endDate) {
        issues.push({
            code: 'DATE_OUTSIDE_PERIOD',
            message: 'posting date ' + postingDate + ' is outside period ' + period.id,
            details: { start: period.startDate, end: period.endDate },
        });
    }
    return issues;
}
/** الخزينة لا تصبح سالبة إلا بسياسة معتمدة صريحة. */
function checkCashboxes(lines, ctx) {
    if (ctx.allowNegativeCashbox || !ctx.cashboxBalances)
        return [];
    const delta = new Map();
    for (const line of lines) {
        const account = ctx.accounts.get(line.accountCode);
        if (!account || account.subledger !== 'cashbox' || !line.cashboxId)
            continue;
        const current = delta.get(line.cashboxId) ?? money_1.Money.zero();
        delta.set(line.cashboxId, current.add(line.debit).sub(line.credit));
    }
    const issues = [];
    for (const [cashboxId, change] of delta) {
        const opening = ctx.cashboxBalances.get(cashboxId) ?? money_1.Money.zero();
        const closing = opening.add(change);
        if (closing.isNegative()) {
            issues.push({
                code: 'CASHBOX_NEGATIVE',
                message: 'cashbox ' + cashboxId + ' would go negative',
                details: { opening: opening.toString(), closing: closing.toString() },
            });
        }
    }
    return issues;
}
//# sourceMappingURL=invariants.js.map